import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import archer from '@/assets/home/components/soliders/archer.webp';
import fireMage from '@/assets/home/components/soliders/fire-mage.webp';
import gunner from '@/assets/home/components/soliders/gunner.webp';
import iceMage from '@/assets/home/components/soliders/ice-mage.webp';

interface LoadingProps {
  text?: string;
  size?: 'sm' | 'md' | 'lg';
  fullScreen?: boolean;
}

const soldiers = [archer, fireMage, gunner, iceMage];

export const Loading = ({
  text,
  size = 'md',
  fullScreen = false,
}: LoadingProps) => {
  const [currentSoldierIndex, setCurrentSoldierIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSoldierIndex((prev) => (prev + 1) % soldiers.length);
    }, 800); // Change soldier every 800ms

    return () => clearInterval(interval);
  }, []);

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };

  const textSizeClasses = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-xl',
  };

  const containerClasses = fullScreen
    ? 'w-full h-full relative overflow-hidden flex flex-col items-center justify-center'
    : 'flex flex-col items-center justify-center p-8';

  return (
    <div className={containerClasses}>
      {/* Container for Spinner and Soldier */}
      <div className={`${sizeClasses[size]} relative`}>
        {/* Animated Outer Ring */}
        <motion.div
          className="absolute inset-0 rounded-full"
          animate={{ rotate: 360 }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: 'linear',
          }}
          style={{
            background:
              'conic-gradient(from 0deg, #FF0080 0%, #7928CA 25%, #FF0080 50%, #FFAA00 75%, #FF0080 100%)',
            opacity: 0.9,
          }}
        />
        {/* Inner Circle with Soldier */}
        <div
          className="absolute inset-1 rounded-full overflow-hidden flex items-center justify-center backdrop-blur-md"
          style={{
            background: 'rgba(26, 26, 46, 0.6)',
          }}
        >
          <motion.img
            key={currentSoldierIndex}
            src={soldiers[currentSoldierIndex]}
            alt="soldier"
            className="w-[80%] h-[80%] object-contain"
            initial={{ y: 0 }}
            animate={{
              y: [5, -5, 5],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 0.6,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </div>
      </div>

      {/* Loading Dots Animation */}
      {text && (
        <motion.div
          className={`mt-4 flex items-center gap-1 ${textSizeClasses[size]}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          <span className="text-white font-normal">{text}</span>
          <motion.span
            className="text-white"
            animate={{ opacity: [0, 1, 0] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: 0,
            }}
          >
            .
          </motion.span>
          <motion.span
            className="text-white"
            animate={{ opacity: [0, 1, 0] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: 0.3,
            }}
          >
            .
          </motion.span>
          <motion.span
            className="text-white"
            animate={{ opacity: [0, 1, 0] }}
            transition={{
              duration: 1.5,
              repeat: Infinity,
              delay: 0.6,
            }}
          >
            .
          </motion.span>
        </motion.div>
      )}
    </div>
  );
};
